import { useState, useEffect } from 'react';
import { OAuthError, postData, putData } from '../backend';
import { Switch, Route, useHistory, Link } from 'react-router-dom';
import useUser from '../hooks/useUser';
import AppOnboarding from './AppPortal';
import AppAutoLogin from './AppAutoLogin';
import WorkflowList from './WorkflowList';
import TemplateList from './TemplateList';
import TemplateDetail from './TemplateDetail';
import Settings from './Settings';
import Page404 from './Page404';
import WorkflowDetail from './WorkflowDetail';
import AppBanner from './AppBanner';
import AppBannerHubspot from './AppBannerHubspot';
import PayWallModalTrial from './PayWallModalTrial';
import HubspotLanding from './HubspotLanding';
import LoadingModal from './LoadingModal';
import { useModal } from '../contexts';
import { Source, Document, Workflow, BasicWorkflow } from '../interfaces';
import OAuthModalHubspot from './OAuthModalHubspot';
import OAuthModalXero from './OAuthModalXero';
import OAuthModal from './OAuthModal';
import ErrorModal from './ErrorModal';
import useWorkflows from '../hooks/useWorkflows';
import WorkflowModalEdit from './WorkflowModalEdit';
import { useTranslation } from 'react-i18next';
import AcceptInviteModal from './AcceptInviteModal';
import Sidebar from './Sidebar';
import UserPanel from './User';
import Home from './Home';
import WorkflowOutputs from './WorkflowOutputs';

function AppWorkflow() {
  const { user } = useUser();
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal();

  const [hasUpdatedUser, setHasUpdatedUser] = useState(false);

  const history = useHistory();
  const { results: workflows } = useWorkflows().data;

  const urlSearchParams = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(urlSearchParams.entries());

  const [showBanner, setShowBanner] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const [showHubspotBanner, setShowHubspotBanner] = useState(localStorage.getItem("hideHubspotBanner2025") !== "true");

  const referralToken = params["referral"];
  if (referralToken)
    localStorage.setItem("referralToken", referralToken);

  useEffect(() => {
    // TODO: Figure out where this stuff lives

    if (!user || hasUpdatedUser)
      return;

    setHasUpdatedUser(true);

    if (window.location.pathname.startsWith("/signup"))
      window.location.pathname = "/";

    const referralToken = localStorage.getItem("referralToken");
    if (referralToken && !user.referredBy)
      postData(`/auth/referral/`, { referralToken })
        .catch(console.error);

    setShowBanner(user.subscription?.tier === "trial");
    setShowUpgradeModal(user.subscription?.tier === "free" && !localStorage.getItem("dontShowUpgradeModal"));

    if (user.pendingInvites.length > 0)
      openModal(<AcceptInviteModal invite={user.pendingInvites[0]} />);

    // Set login hints
    localStorage.setItem("lastEmail", user.email);
    localStorage.setItem("lastAuthProvider", user.authProvider);
  }, [user, hasUpdatedUser, openModal]);

  const bannerHeight = 56;
  const contentOffset = ((showBanner || showHubspotBanner) ? bannerHeight : 0);

  async function createNewWorkflow(stage: "SOURCE" | "DOCUMENT", blockType: string, file?: any, isPdf?: boolean, options?: Record<string, any>) {
    const initialWorkflow = { "color": "#cccccc", "icon": "DocumentText", name: file?.name ?? t("New Workflow"), description: "" };

    openModal(
      <WorkflowModalEdit
        workflow={initialWorkflow as Workflow}
        updateWorkflow={(workflow: BasicWorkflow) => createNewWorkflowWithDetails(stage, blockType, file, isPdf, workflow, options)}
        acceptText={t("Create")}
        titleText={t("Create Workflow")}
      />
    );
  }

  async function createNewWorkflowWithDetails(stage: "SOURCE" | "DOCUMENT", blockType: string, file?: any, isPdf?: boolean, workflowDetails?: BasicWorkflow, options?: Record<string, any>) {
    //@ts-ignore
    const dataLayer = window.dataLayer;

    const subtitle = !!file
      ? t("This may take a minute as we retreive the file from your Drive.")
      : t("This may take a minute as we create a new file in your Drive.");

    openModal(<LoadingModal title={`${t("Creating New Workflow")}: ${workflowDetails!.name}`} subtitle={subtitle} />)

    const workflow: Workflow = await postData("/workflows/", workflowDetails);

    try {
      if (stage === "SOURCE") {
        const sourceType = blockType;
        const sourceOptions = { sourceType, ...(options ?? {}) };

        const source: Source = await postData(`/workflows/${workflow.id}/source/`, sourceOptions);

        if (sourceType === "MICROSOFT_EXCEL" && file?.id)
          await putData(`/microsoft/source-config/${source.microsoftExcelConfig!.id}/`, { fileId: file.id });

        else if (file?.id)
          source.file = await putData(`/workflows/sources/${source.id}/file/`, { file });

        else if (["GOOGLE_SHEETS", "GOOGLE_FORMS"].includes(sourceType)) // Create file only for Google source types
          source.file = await postData(`/workflows/sources/${source.id}/file/`);

        if (sourceType === "GOOGLE_FORMS") // Enable auto-create
          putData(`/workflows/${workflow.id}/auto-create/`, { autoCreate: true });

        dataLayer.push({ 'source_type': sourceType, "document_type": undefined });
      }

      else if (stage === "DOCUMENT") {
        const documentType = blockType;
        const document: Document = await postData(`/workflows/${workflow.id}/documents/`, { documentType });

        if (documentType.startsWith("MICROSOFT"))
          await putData(`/microsoft/document-config/${document.microsoftDocumentConfig!.id}/`, { fileId: file.id });

        else if (file) {
          if (isPdf) {
            const content = new Blob([file], { type: "application/pdf" });

            const { url } = await postData(`/workflows/documents/${document!.id}/generate-pdf-upload-url/`);

            await fetch(url, {
              method: "PUT",
              headers: {
                "content-type": "application/pdf"
              },
              body: content
            });

            await postData(`/workflows/documents/${document!.id}/confirm-pdf-upload-status/`, { filename: file.name });
          } else
            document.file = await putData(`/workflows/documents/${document.id}/file/`, { file });
        }

        else if (documentType.startsWith("GOOGLE")) // Create file only for Google document types
          document.file = await postData(`/workflows/documents/${document.id}/file/`);

        dataLayer.push({ "source_type": undefined, 'document_type': documentType });
      }

      closeModal();

      if ((workflows?.length ?? 0) <= 1)
        history.push("/workflow/");
      else
        history.push("/w/" + workflow.id + ((stage === "SOURCE" && blockType !== "SIGNATURE_LINK") ? "/source" : "/document"));

      dataLayer.push({ 'event': 'create_workflow', 'workflow_id': workflow.id, 'workflow_name': workflow.name });

    } catch (error) {
      if (error instanceof OAuthError)
        if (error.oauthUrl.includes("app.hubspot"))
          openModal(<OAuthModalHubspot oauthUrl={error.oauthUrl} onClose={() => history.push(`/w/${workflow.id}`)} />);
        else if (error.oauthUrl.includes("xero"))
          openModal(<OAuthModalXero oauthUrl={error.oauthUrl} onClose={() => history.push(`/w/${workflow.id}`)} />);
        else
          openModal(<OAuthModal oauthUrl={error.oauthUrl} />);
      else
        openModal(<ErrorModal details={error} />);
    }

    return workflow;
  }

  return (
    <Switch>
      <Route path="/auto-login" component={AppAutoLogin} />
      <Route path="/signup" component={AppOnboarding} />
      <Route path="*">
        <div className='flex flex-col h-screen w-screen overflow-hidden font-sans bg-white'>
          {(user?.subscription?.tier === "free" || user?.subscription?.tier === "trial") &&
            <PayWallModalTrial isShown={showUpgradeModal} setIsShown={setShowUpgradeModal} />
          }
          <AppBanner isVisible={showBanner} setIsVisible={setShowBanner} style={{ height: bannerHeight }} openUpgradeModal={() => setShowUpgradeModal(true)} />
          <AppBannerHubspot isVisible={showHubspotBanner} setIsVisible={(visible) => { localStorage.setItem("hideHubspotBanner2025", String(!visible)); setShowHubspotBanner(visible) }} height={bannerHeight} />
          <div className='flex'>
            <Sidebar />
            <div className='flex flex-col h-full w-full'>
              <Switch>
                <Route path="/workflow"> {/* defaults to latest workflow via "__latest" id */}
                  <WorkflowDetail contentOffset={contentOffset} />
                </Route>
                <Route path="/w/:id">
                  <WorkflowDetail contentOffset={contentOffset} />
                </Route>
                <Route path="*">
                  {/* Header Section */}
                  <div className="bg-white border-b border-gray-200 h-[56px] flex items-center justify-between px-6">
                    <Link to='/' className="flex items-center gap-2">
                      <img className="h-6 w-6" src="https://static.portant.co/logo-mark-blue.svg" alt="Portant Logo" />
                      <h1 className="text-xl font-causten font-semibold text-black">Portant</h1>
                    </Link>
                    <UserPanel />
                  </div>
                  <div className='flex flex-row flex-1'>
                    <div className='flex flex-col w-full overflow-y-auto bg-gray-50 p-4 scrollbar-hidden' style={{ height: `calc(100vh - 56px - ${contentOffset}px)` }}>
                      <Switch>
                        <Route path="/" exact>
                          <Home contentOffset={contentOffset} createNewWorkflow={createNewWorkflow} />
                        </Route>
                        <Route path="/workflows">
                          <WorkflowList contentOffset={contentOffset} createNewWorkflow={createNewWorkflow} />
                        </Route>
                        <Route path="/outputs">
                          <WorkflowOutputs contentOffset={contentOffset} />
                        </Route>
                        <Route path="/hubspot-from-scratch" component={HubspotLanding} exact />
                        <Route path='/templates/:id' component={TemplateDetail} />
                        <Route path='/templates'>
                          <TemplateList contentOffset={contentOffset} />
                        </Route>
                        {user &&
                          <Route path='/settings'>
                            <Settings contentOffset={contentOffset} />
                          </Route>
                        }
                        <Route path='*' component={Page404} />
                      </Switch>
                    </div>
                  </div>
                </Route>
              </Switch>
            </div>
          </div>
        </div>
      </Route>
    </Switch>
  );
}

export default AppWorkflow;
